import { Component, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, Subscription } from 'rxjs';

import { AddonService } from '@shared/services/addon.service';
import { CategoryService } from '@shared/services/category.service';
import { CustomEventService } from '@shared/services/custom-event.service';
import { DatasourceService } from '@shared/services/datasource.service';

import { DatasourceListModalComponent } from '@shared/components/modals/datasource-list-modal/datasource-list-modal.component';
import { DeleteConfirmModalComponent } from '@shared/components/modals/delete-confirm-modal/delete-confirm-modal.component';

import ActionTypesEnum from '@shared/enums/action-constant.enum';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NzModalRef, NzModalService  } from 'ng-zorro-antd/modal';
import { NzNotificationService  } from 'ng-zorro-antd/notification';
import { ActivatedRoute } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';

@UntilDestroy()
@Component({
  selector: 'app-data-sources',
  templateUrl: './data-sources.component.html'
})
export class DataSourcesComponent implements OnInit, OnDestroy {

  DESCRIPTION_LENGTH = 65;

  categories: Addon.Category[];
  addons: Addon.AddonModel[];
  addOnGroups: Addon.AddonGroup[];
  loading: boolean;
  modal: NzModalRef;
  notificationDescription: string;
  dataSourceDeleteModal: NzModalRef;
  dataSources: Addon.DatasourceListItem[];
  tempAddOn: Addon.AddonModel;

  customEventSubscription: Subscription;

  constructor(
    private addonService: AddonService,
    private categoryService: CategoryService,
    private customEventService: CustomEventService,
    private datasourceService: DatasourceService,
    private modalService: NzModalService,
    private notificationService: NzNotificationService,
    private activatedRoute: ActivatedRoute,
    private message: NzMessageService
  ) { }

  ngOnInit(): void {
    this.loading = true;

    forkJoin({
      addons: this.addonService.list({active: true, hasVersion: true}),
      categories: this.categoryService.list()
    }).pipe(untilDestroyed(this))
      .subscribe(({addons, categories}: {addons: Addon.AddonModel[], categories: Addon.AddonGroup[]}) => {
        this.addons = addons;
        this.categories = categories.splice(1,categories.length);
        this.loading = false;
        this.activatedRoute.queryParams.subscribe(params => {
          const errorMessage = params['errorMessage'];
          if (errorMessage) {
            this.message.create('error', errorMessage);
          } else {
            const addon = params['addon'];
            const selectedAddon = this.addons.filter(item => item.name.toLocaleLowerCase() === addon)[0];
            if (selectedAddon) {
              this.showDatasources(selectedAddon);
            }
          }
        });

        this.addOnGroups = this.categories.map((category: Addon.AddonGroup) => {
          category.addons = this.addons.filter((addon: Addon.AddonModel) => addon.category.id === category.id);
          return category;
        });
      }, error => {
        this.loading = false;
        console.log('error occurred while fetching initial data for marketplace', error);
      });

    this.customEventSubscription = this.customEventService.eventAnnounced$
      .pipe(untilDestroyed(this)).subscribe(
      ({ action, data }) => {
        if (action === ActionTypesEnum.ADD_DATASOURCE) {
          const { dataSource, addOn } = data;

          this.addHandler(dataSource, addOn);
        }

        if (action === ActionTypesEnum.EDIT_DATASOURCE) {
          const { dataSource, addOn } = data;

          this.editHandler(dataSource, addOn);
        }

        if (action === ActionTypesEnum.DELETE_CONFIRM) {
          this.removeHandler(data);
        }

        if (action === ActionTypesEnum.SHOW_DELETE_CONFIRM_MODAL) {
          const { dataSource, addon } = data;
          this.tempAddOn = addon;
          this.dataSourceDelete(dataSource);
        }

        if (action === ActionTypesEnum.GET_DATASOURCE_DETAIL) {
          this.datasourceService.getDatasource(data)
            .pipe(untilDestroyed(this)).subscribe(response => {
            this.customEventService.announceEvent(ActionTypesEnum.SET_DATASOURCE_DETAIL, response);
          }, error => {
            console.log('error while fetching data source details', error);
          })
        }

        if (action === ActionTypesEnum.TEST_CONNECTION) {
          this.connectionHandler(data);
        }

        if (action === ActionTypesEnum.CLOSE_MODAL) {
          if (this.modal) {
            this.modal.close();
          }
          if(this.dataSourceDeleteModal) {
            this.dataSourceDeleteModal.close();
          }
        }
      });

  }

  showDatasources(addOn: Addon.AddonModel) {
    if (addOn.loading) {
      return;
    }

    addOn.loading = true;
    this.addonService.getDatasources(addOn.id)
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        addOn.loading = false;
        addOn.datasources = response;
        this.dataSources = response;

        this.modal = this.modalService.create({
          nzMaskClosable: false,
          nzContent: DatasourceListModalComponent,
          nzComponentParams: {
            addOn
          },
          nzWidth: '450px',
          nzFooter: null
        });
      }, error => {
        addOn.loading = false;
        console.log('error while fetching addOn data sources', error);
      });
  }

  dataSourceDelete(dataSource: Addon.DatasourceListItem): void {
    this.dataSourceDeleteModal = this.modalService.create({
      nzMaskClosable: false,
      nzContent: DeleteConfirmModalComponent,
      nzWidth: '450px',
      nzFooter: null,
      nzComponentParams: {
        title: `Delete Data Source '${ dataSource.name }'`,
        description: `Are you sure you want to delete this '${ dataSource.name }'`,
        inputLabel: `Confirm '<strong>${ dataSource.name }</strong>' name`,
        value: dataSource.name,
        buttonText: `Delete Data Source`,
        data: dataSource.id
      }
    });
  }

  editHandler(dataSource: DataSource.DataSourceModel, addOn: Addon.DatasourceListItem): void {
    const innerComponent = this.modal.getContentComponent();
    innerComponent.loading = true;

    this.datasourceService.editDataSource(dataSource)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.notificationDescription = `${addOn.displayName} ${dataSource.version ? dataSource.version : ''} is successfully edited.`;

        innerComponent.loading = false;

        this.showMessage();
      }, error => {
        console.log('error while updating a data source', error);
        innerComponent.loading = false;
      });
  }

  removeHandler(dataSourceId: string): void {
    const innerComponent = this.dataSourceDeleteModal.getContentComponent();

    innerComponent.loading = true;
    this.datasourceService.deleteDataSource(dataSourceId)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        innerComponent.loading = false;
        this.showDatasources(this.tempAddOn);
      }, error => {
        innerComponent.loading = false;
        console.log('error while updating a data source', error);
      });
  }

  expandGroup(group: Addon.AddonGroup): void {
    group.isExpanded = true;
  }

  showMessage(): void {
    setTimeout((): void => {
      this.modal.destroy();
      this.notificationService.create('success', 'Success!',
        this.notificationDescription);
    }, 500);
  }

  addHandler(data: DataSource.DataSourceModel, addOn: Addon.AddonModel): void {
    const innerComponent = this.modal.getContentComponent();
    innerComponent.loading = true;

    this.datasourceService.addDataSource(data)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.notificationDescription = `${addOn.displayName} ${data.version ? data.version : ''}  is successfully added to DataSources.`;
        innerComponent.loading = false;
        addOn.hasDatasource = true;

        this.showMessage();
      }, error => {
        console.log('error while adding a data source', error);
        innerComponent.loading = false;
      });
  }

  connectionHandler(connectData: DataSource.DataSourceModel): void {
    const innerComponent = this.modal.getContentComponent();
    innerComponent.testLoading = true;

    this.datasourceService.testDatasource(connectData)
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        innerComponent.testLoading = false;
        innerComponent.testConnectionResult = result.result;
      }, error => {
        if(error.error.message) {
          innerComponent.testConnectionResult = false;
        }
        innerComponent.testLoading = false;
        console.log('connection error', error);
      });
  }

  ngOnDestroy(): void {
    // prevent memory leak when component destroyed
    this.customEventSubscription.unsubscribe();
  }
}
